import { lazy } from "react";
import {
  loadClientSecret,
  loadQuestions,
  loadReferral,
  userTempToken,
  loadAllCourses,
  loadCoursesCategories,
  loadCourseLessons,
  loadAdminCourses,
  loadAdminCourse,
  loadUserInfo,
} from "../loaders/pageLoadable";

import { RequireAuth } from "react-auth-kit";
import LessonAuthWrapper from "../components/LessonAuthWrapper";
import ErrorBoundary from "../modules/shared/components/ErrorBoundary";
import ErrorHandler from "../modules/shared/components/ErrorHandler";
import ApprovalDeclined from "../modules/auth/ApprovalDeclined";
import ExclusiveMembers from "../modules/promote/ExclusiveMembers";
import AffiliateProgram from "../modules/affiliate/AffiliateProgram";
import IncomeHistory from "../modules/affiliate/IncomeHistory";
import InitiateWithdraw from "../modules/affiliate/InitiateWithdraw";
import W9Requests from "../modules/affiliate/W9Requests";
import GettingStarted from "../modules/affiliate/GettingStarted";
import UserActivityExport from "../modules/admin-user-activity/UserActivityExport";
import Funnel from "../modules/promote/Funnel";
import W9Form from "../modules/affiliate/components/w9Form";
import W9Preview from "../modules/affiliate/components/W9Preview";
import UserSingleResponse from "../modules/user-challenges/UserSingleResponse";
import Blueprint from "../modules/promote/Blueprint";

const Register = lazy(() => import("../modules/auth/Register"));
const Login = lazy(() => import("../modules/auth/Login"));
const ApprovalPending = lazy(() => import("../modules/auth/ApprovalPending"));
const ForgotPassword = lazy(() => import("../modules/auth/ForgotPassword"));
const Thankyou = lazy(() => import("../pages/Thankyou"));
const ResetPassword = lazy(() => import("../modules/auth/ResetPassword"));
const Welcome = lazy(() => import("../modules/lead/Welcome"));
const BlueprintWelcome = lazy(() => import("../modules/lead/BlueprintWelcome"));
const BlueprintPublic = lazy(() => import("../modules/lead/BlueprintPublic"));
const Webinar = lazy(() => import("../modules/lead/Webinar"));
const Home = lazy(() => import("../pages/Welcome"));

/*** Portal Pages ***/
const Dashboard = lazy(() => import("../modules/dashboard/Dashboard"));
const Question = lazy(() => import("../modules/questionnaire/Question"));
const Answer = lazy(() => import("../modules/questionnaire/Answer"));
const Courses = lazy(() => import("../modules/courses/Courses"));
const WatchLessons = lazy(() => import("../modules/courses/WatchLessons"));
const MeetingSchedule = lazy(() =>
  import("../modules/questionnaire/MeetingSchedule")
);
const GuestMeetingSchedule = lazy(() =>
  import("../modules/questionnaire/GuestMeetingSchedule")
);
const AdminCourseListing = lazy(() =>
  import("../modules/admin-courses/CourseListing")
);
const AdminCourseGenerator = lazy(() =>
  import("../modules/admin-courses/CourseGenerator")
);
const AdminQuickLinks = lazy(() =>
  import("../modules/admin-quick-links/QuickLinksListing")
);
const AdminGuestUserRequests = lazy(() =>
  import("../modules/admin-guest-user-requests/GuestUsersListing")
);

/***Admin Challenges Pages ***/
const Challenges = lazy(() =>
  import("../modules/challenges/ChallengesDashboard")
);
const CreateChallenges = lazy(() =>
  import("../modules/challenges/CreateChallenges")
);
const EditChallenge = lazy(() => import("../modules/challenges/EditChallenge"));
const CreateChallengesLaunchProcess = lazy(() =>
  import("../modules/challenges/CreateChallengesLaunchProcess")
);
const ChallengesListing = lazy(() =>
  import("../modules/challenges/ChallengesListing")
);
const TasksListing = lazy(() => import("../modules/challenges/TasksListing"));
const UserTaskResponse = lazy(() =>
  import("../modules/challenges/components/UserTaskResponse")
);
const ChallengesLeaderBoard = lazy(() =>
  import("../modules/challenges/LeaderBoard")
);

/***User Challenges Pages ***/
const UserChallenges = lazy(() =>
  import("../modules/user-challenges/UserChallengesDashboard")
);
const UserChallengesListing = lazy(() =>
  import("../modules/user-challenges/UserChallengesListing")
);
const UserTaskListing = lazy(() =>
  import("../modules/user-challenges/UserTaskListing")
);
const UserChallengesLeaderBoard = lazy(() =>
  import("../modules/user-challenges/UserLeaderBoard")
);
const UserChallengeResponse = lazy(() =>
  import("../modules/user-challenges/UserChallengeResponse")
);

/*** Admin Guest Requests Pages ***/
const Pending = lazy(() => import("../modules/guest-user/Pending"));
const Approved = lazy(() => import("../modules/guest-user/Approved"));
const Declined = lazy(() => import("../modules/guest-user/Declined"));

/*** Error Pages ***/
const Error404 = lazy(() => import("../pages/Error404"));
const Error500 = lazy(() => import("../pages/Error500"));
const ProtectedRoutes = lazy(() => import("../pages/ProtectedRoutes"));
const Unauthorized = lazy(() => import("../pages/Unauthorized"));

/*** Promote Pages ***/
// const Introduction = lazy(() => import("../modules/promote/Introduction"));
const Setting = lazy(() => import("../modules/promote/Setting"));
const PromoteFunnel = lazy(() => import("../modules/promote/Funnel"));
const Stats = lazy(() => import("../modules/promote/Stats"));
const FunnelLink = lazy(() => import("../modules/promote/FunnelLink"));
const Guests = lazy(() => import("../modules/promote/Guests"));
const Members = lazy(() => import("../modules/promote/Members"));
const Leads = lazy(() => import("../modules/promote/Leads"));

/*** Calendar Pages ***/
const Calendar = lazy(() => import("../modules/calendar/Calendar"));

/*** Profile Pages ***/
const Profile = lazy(() => import("../modules/account/Profile"));
const Payment = lazy(() => import("../modules/account/Payment"));
const AdvisorSetting = lazy(() => import("../modules/account/AdvisorSetting"));
const UpdatePassword = lazy(() => import("../modules/account/UpdatePassword"));
const UserInfo = lazy(() => import("../modules/account/UserInfo"));

/*** Advisor Questionnaires Pages ***/
const AdvisorQuestionnaires = lazy(() =>
  import("../modules/questionnaire/AdvisorQuestionnaires")
);

/*** Welcome Videos Pages ***/
const AdvisorWelcomeVideos = lazy(() =>
  import("../modules/welcome-videos/AdvisorWelcomeVideos")
);

/*** Notification Pages ***/
const AllNotification = lazy(() =>
  import("../modules/notifications/AllNotification")
);
const CreateNotification = lazy(() =>
  import("../modules/admin-notifications/AdminNotification")
);
/*** Support Page ***/
const Support = lazy(() => import("../modules/support/Support"));

/*** Leaderboard Page ***/
const Leaderboard = lazy(() => import("../modules/leaderboard/Leaderboard"));

/*** Remove Account Page ***/
const DeleteAccountTerms = lazy(() =>
  import("../modules/delete-account-terms/DeleteAccountTerms")
);

/*** Account Terms Page ***/
const AccountTerms = lazy(() =>
  import("../modules/account-terms/AccountTerms")
);

/*** Community Pages ***/
const Community = lazy(() => import("../modules/community/Community"));
const CommunityPost = lazy(() => import("../modules/community/CommunityPost"));

/*** Quick Link Page ***/
const QuickLinks = lazy(() => import("../modules/quick-links/QuickLinks"));

/*** Launch Process Page ***/
const LaunchProcess = lazy(() => import("../modules/launch-process/LaunchProcess"));
const TraineeLaunchProcessChallengeView = lazy(() => import("../modules/launch-process/TraineeLaunchProcessChallengeView"));
const ChallengeResponsesView = lazy(() => import("../modules/launch-process/ChallengeResponsesView"));
const LaunchProcessLeaderboard = lazy(() => import("../modules/launch-process/LaunchProcessLeaderboard"));

export const routes = [
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/welcome",
    element: <Welcome />,
    loader: loadReferral,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
  },
  {
    path: "/blueprint",
    element: <BlueprintPublic />,
  },
  {
    path: "/blueprint/:uuid",
    element: <BlueprintPublic />,
  },
  {
    path: "/webinar",
    element: <Webinar />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/pending-approval",
    element: <ApprovalPending />,
  },
  {
    path: "/declined-approval",
    element: <ApprovalDeclined />,
  },
  {
    path: "/register",
    element: <Register />,
    loader: loadClientSecret,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
  },
  {
    path: "/thank-you",
    element: <Thankyou />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/password-reset/:resetToken",
    element: <ResetPassword />,
  },
  {
    path: "/dashboard",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Dashboard />
      </RequireAuth>
    ),
  },
  {
    path: "/questionnaire",
    loader: loadQuestions,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <Question />
      </RequireAuth>
    ),
  },
  {
    path: "/support",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Support />
      </RequireAuth>
    ),
  },
  {
    path: "/answer/:user_id",
    loader: loadQuestions,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <Answer />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-links",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Funnel />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-links/setting",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Setting />
      </RequireAuth>
    ),
  },
  {
    path: "/promote/funnel",
    element: (
      <RequireAuth loginPath={"/login"}>
        <PromoteFunnel />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-links/statistics",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Stats />
      </RequireAuth>
    ),
  },
  {
    path: "/blueprint-content",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Blueprint />
      </RequireAuth>
    ),
  },
  {
    path: "/blueprint-content/:uuid",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Blueprint />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program",
    element: (
      <RequireAuth loginPath={"/login"}>
        <AffiliateProgram />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/initiate-withdraw",
    element: (
      <RequireAuth loginPath={"/login"}>
        <InitiateWithdraw />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/income-history",
    element: (
      <RequireAuth loginPath={"/login"}>
        <IncomeHistory />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/getting-started",
    element: (
      <RequireAuth loginPath={"/login"}>
        <GettingStarted />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/initiate-withdraw/w9-form",
    element: (
      <RequireAuth loginPath={"/login"}>
        <W9Form />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/initiate-withdraw/w9-form/preview",
    element: (
      <RequireAuth loginPath={"/login"}>
        <W9Preview />
      </RequireAuth>
    ),
  },
  {
    path: "/promote/funnel/:funnelType",
    element: (
      <RequireAuth loginPath={"/login"}>
        <FunnelLink />
      </RequireAuth>
    ),
  },
  {
    path: "/calendar",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Calendar />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/guest",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Guests />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/members",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Members />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/exclusive-member",
    element: (
      <RequireAuth loginPath={"/login"}>
        <ExclusiveMembers />
      </RequireAuth>
    ),
  },
  {
    path: "/affiliate-program/leads",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Leads />
      </RequireAuth>
    ),
  },
  {
    path: "/admin/guest-user/pending-request",
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <Pending />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/admin/guest-user/approved-request",
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <Approved />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/admin/guest-user/declined-request",
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <Declined />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/profile/:userId",
    loader: loadUserInfo,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserInfo />
      </RequireAuth>
    ),
  },
  {
    path: "/meeting-schedule/:userId",
    loader: userTempToken,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: <MeetingSchedule />,
  },
  {
    path: "/guest-meeting-schedule/:userId",
    loader: userTempToken,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: <GuestMeetingSchedule />,
  },
  {
    path: "/admin/courses",
    loader: loadAdminCourses,
    errorBoundary: <ErrorBoundary />,
    errorElement: <ErrorHandler />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <AdminCourseListing />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/admin/courses/:courseId",
    loader: loadAdminCourse,
    errorBoundary: <ErrorBoundary />,
    errorElement: <ErrorHandler />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <AdminCourseGenerator />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/courses",
    loader: loadAllCourses,
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <Courses />
      </RequireAuth>
    ),
  },
  {
    path: "/courses/:courseId/lessons/:lessonId?",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <LessonAuthWrapper>
        <WatchLessons />
      </LessonAuthWrapper>
    ),
  },
  {
    path: "/quick-links",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <QuickLinks />
      </RequireAuth>
    ),
  },
  {
    path: "/admin-quick-links",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <AdminQuickLinks />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/admin/w9-form-requests",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <W9Requests />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/admin/user-activity",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <UserActivityExport />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/guest-user-requests",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <AdminGuestUserRequests />
      </RequireAuth>
    ),
  },
  {
    path: "/account",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Profile />
      </RequireAuth>
    ),
  },
  {
    path: "/account/settings",
    element: (
      <RequireAuth loginPath={"/login"}>
        <AdvisorSetting />
      </RequireAuth>
    ),
  },
  {
    path: "/account/security",
    element: (
      <RequireAuth loginPath={"/login"}>
        <UpdatePassword />
      </RequireAuth>
    ),
  },
  {
    path: "/account/payment",
    loader: loadClientSecret,
    element: (
      <RequireAuth loginPath={"/login"}>
        <Payment />
      </RequireAuth>
    ),
  },
  {
    path: "/leaderboard",
    element: (
      <RequireAuth loginPath={"/login"}>
        <Leaderboard />
      </RequireAuth>
    ),
  },
  {
    path: "/create-notifications",
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <CreateNotification />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/notifications",
    element: (
      <RequireAuth loginPath={"/login"}>
        <AllNotification />
      </RequireAuth>
    ),
  },
  {
    path: "/advisor-questionnaire",
    element: (
      <RequireAuth loginPath={"/login"}>
        <AdvisorQuestionnaires />
      </RequireAuth>
    ),
  },
  {
    path: "/welcome-videos",
    element: (
      <RequireAuth loginPath={"/login"}>
        <AdvisorWelcomeVideos />
      </RequireAuth>
    ),
  },
  {
    path: "/challenges",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <Challenges />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/challenges/create",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <CreateChallenges />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/challenges/edit/:uuid",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ProtectedRoutes>
          <EditChallenge />
        </ProtectedRoutes>
      </RequireAuth>
    ),
  },
  {
    path: "/challenges/listing",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ChallengesListing />
      </RequireAuth>
    ),
  },
  {
    path: "/tasks-listing",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <TasksListing />
      </RequireAuth>
    ),
  },
  {
    path: "/tasks-response",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserTaskResponse />
      </RequireAuth>
    ),
  },
  {
    path: "/challenges/leaderboard",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ChallengesLeaderBoard />
      </RequireAuth>
    ),
  },
  {
    path: "/user-challenges",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserChallenges />
      </RequireAuth>
    ),
  },
  {
    path: "/user-challenges/challenges",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserChallengesListing />
      </RequireAuth>
    ),
  },
  {
    path: "/user-challenges/tasks",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserTaskListing />
      </RequireAuth>
    ),
  },
  {
    path: "/user-challenges/response",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserChallengeResponse />
      </RequireAuth>
    ),
  },
  {
    path: "/challenges/:uuid/response",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserSingleResponse />
      </RequireAuth>
    ),
  },
  {
    path: "/user-challenges/leaderboard",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <UserChallengesLeaderBoard />
      </RequireAuth>
    ),
  },
  {
    path: "/community",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <Community />
      </RequireAuth>
    ),
  },
  {
    path: "/community/:post_uuid/post",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <CommunityPost />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <LaunchProcess />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process/challenges/create",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <CreateChallengesLaunchProcess />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process/challenges/edit/:uuid",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <CreateChallengesLaunchProcess />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process/challenges/view",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <TraineeLaunchProcessChallengeView />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process/challenges/:uuid/responses",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <ChallengeResponsesView />
      </RequireAuth>
    ),
  },
  {
    path: "/launch-process/leaderboard",
    errorBoundary: <ErrorBoundary />,
    errorElement: <Error500 />,
    element: (
      <RequireAuth loginPath={"/login"}>
        <LaunchProcessLeaderboard />
      </RequireAuth>
    ),
  },
  {
    path: "/server",
    element: <Error500 />,
  },
  {
    path: "*",
    element: <Error404 />,
  },
  {
    path: "/delete-account-terms",
    element: <DeleteAccountTerms />,
  },
  {
    path: "/account-terms",
    element: <AccountTerms />,
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />,
  },
  {
    path: "/firebase-messaging-sw.js",
    element: <Unauthorized />,
  },
];
